import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import BreakfastLanding from './BreakfastLanding';
import { getMarketplaceState } from './marketplaceApi';

/**
 * `/marketplace` entry. Visitors get the public marketing landing. Signed-in
 * marketplace users have NO dashboard — they're routed by where they are in the
 * journey: a free user (free study unused) lands on the free study flow, a
 * returning/paid user lands on the "what would you like to do next?" conversion
 * screen (so full-access is always one step away).
 */
const MarketplaceHome: React.FC = () => {
  const { isAuthenticated, loading } = useMarketplaceAuth();
  const [freeUsed, setFreeUsed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    // Fail open to the free study page if the request rejects OR hangs (a stalled
    // /me/state must not leave the user on a blank screen forever).
    const timeout = setTimeout(() => { if (active) setFreeUsed(false); }, 10000);
    getMarketplaceState()
      .then((s) => { if (active) setFreeUsed(s.free_study_used); })
      .catch(() => { if (active) setFreeUsed(false); })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); };
  }, [isAuthenticated]);

  if (loading) return null; // brief — avoids flashing the landing to a signed-in user
  if (!isAuthenticated) return <BreakfastLanding />;
  if (freeUsed === null) {
    // Resolving free-vs-paid routing — show a spinner rather than a blank screen.
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  return <Navigate to={freeUsed ? '/marketplace/done' : '/marketplace/run'} replace />;
};

export default MarketplaceHome;
